// src/components/AutocompleteInput.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { geocodeCity } from "../utils/geocode";
import { Box, Input, List, ListItem, Spinner } from "@chakra-ui/react";

//const DEBUG_AC = true;
//const log = (...args) => {
//   if (DEBUG_AC) console.log("[AC]", ...args);};

export default function AutocompleteInput({
    value,
    onChange,
    onSelect,
    city,
    state,
    minLength = 4,
}) {
    const GEOCODE_PROXY = process.env.REACT_APP_GEOCODE_PROXY;

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    // UI immediata
    const [localValue, setLocalValue] = useState(value || "");

    // debounce query
    const [debouncedTerm, setDebouncedTerm] = useState("");

    // bbox città
    const [bbox, setBbox] = useState(null); // {west,north,east,south}

    // refs (no re-render)
    const abortRef = useRef(null);
    const lastCallRef = useRef(0);
    const lastQueryRef = useRef("");
    const cacheRef = useRef(new Map());

    const typingRef = useRef(0);
    const isUserTypingRef = useRef(false);
    const justSelectedRef = useRef(false);

    const bboxKeyRef = useRef(null); // "city|state" dell'ultima bbox calcolata

    // ✅ NUOVO: refs per city/state — aggiornati senza innescare re-render degli effect
    const cityRef = useRef(city);
    const stateRef = useRef(state);
    useEffect(() => {
        cityRef.current = city;
        stateRef.current = state;
    }, [city, state]);

    // ✅ NUOVO: city debouncata SOLO per il bbox (evita geocodeCity su ogni lettera)
    const [debouncedCity, setDebouncedCity] = useState(city || "");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedCity(city || ""), 600);
        return () => clearTimeout(t);
    }, [city]);

    // term sempre da localValue (non dal parent)
    const term = useMemo(() => (localValue || "").trim(), [localValue]);
    const normalizedTerm = useMemo(() => debouncedTerm.trim().toLowerCase(), [debouncedTerm]);

    // 1) Sync parent -> local solo quando NON sto digitando
    useEffect(() => {
        if (isUserTypingRef.current) return;
        setLocalValue(value || "");
    }, [value]);
   

    // 2) — Debounce (parte quando cambia localValue)
    useEffect(() => {
        if (justSelectedRef.current) {
            justSelectedRef.current = false;
            return; // non rilanciare la ricerca appena dopo una selezione
        }
        const t = setTimeout(() => {
            setDebouncedTerm(term);
        }, 700);
        return () => clearTimeout(t);
    }, [term]);
    // 3) BBOX: usa debouncedCity invece di city diretta — evita geocodeCity su ogni lettera
    useEffect(() => {
        let alive = true;
        const c = debouncedCity.trim(); // ✅ usa debouncedCity
        const s = (state || "").trim();
        const key = c ? `${c}|${s}` : null;

        //log("BBOX effect triggered", { city: c, state: s });

        (async () => {
            if (!c) {
                //log("BBOX reset (no city)");
                if (!alive) return;
                setBbox(null);
                bboxKeyRef.current = null;
                setItems([]);
                return;
            }

            // evita chiamate su "M", "Mi"
            if (c.length < 3) {
                //log("BBOX skip short city", c);
                return;
            }

            // già calcolata per questa città+stato
            if (bboxKeyRef.current === key) {
                // log("BBOX skipped (already cached)", key);
                return;
            }

            // lock subito (evita doppie in StrictMode)
            bboxKeyRef.current = key;

            // log("BBOX fetch START", key);

            try {
                const res = await geocodeCity({ city: c, state: s });

                if (!alive) return;

                // se geocodeCity torna bbox object {south,north,west,east}
                const bb = res?.bbox;

                if (bb && typeof bb === "object") {
                    setBbox({
                        west: parseFloat(bb.west),
                        north: parseFloat(bb.north),
                        east: parseFloat(bb.east),
                        south: parseFloat(bb.south),
                    });
                    //  log("BBOX fetch SUCCESS", { key, bbox: bb });
                } else {
                    setBbox(null);
                    bboxKeyRef.current = null;
                    //  log("BBOX fetch EMPTY (no bbox)", { key, got: bb });
                }
            } catch (e) {
                if (!alive) return;
                setBbox(null);
                bboxKeyRef.current = null;
                //log("BBOX fetch ERROR", e);
            }
        })();

        return () => {
            alive = false;
            // log("BBOX cleanup");
        };
    }, [debouncedCity, state]); // ✅ debouncedCity invece di city


    // 4) Fetch suggestions — dipende SOLO da debouncedTerm
    //    city/state letti dai ref al momento dell'esecuzione, non nei deps
    useEffect(() => {

        if (!debouncedTerm || debouncedTerm.length < minLength) {
            setItems([]);
            return;
        }

        const c = cityRef.current?.trim(); // ✅ legge dal ref, non dalla prop
        if (!c || c.length < 3) {
            setItems([]);
            return;
        }

        // evita doppia query identica
        const queryKey = `${debouncedTerm}|${c}`;

        if (queryKey === lastQueryRef.current) {
            // log("SKIP: same query");
            return;
        }

        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        (async () => {
            setLoading(true);

            try {
                const query = `${debouncedTerm} ${c} Italia`;

                // log("FETCH START", query);

                const r = await fetch(
                    `${GEOCODE_PROXY}?q=${encodeURIComponent(query)}`,
                    { signal: controller.signal }
                );

                // ✅ guard Content-Type
                const contentType = r.headers.get("content-type") || "";
                const arr = (r.ok && contentType.includes("application/json"))
                    ? await r.json()
                    : [];

                // log("RESULTS RECEIVED", arr.length);

                lastQueryRef.current = queryKey;

                // Filtra solo risultati con una vera via/strada
                const results = arr.filter(x => {
                    const a = x.address || {};
                    const hasRoad = !!(a.road || a.pedestrian || a.path || a.footway || a.street || a.cycleway);
                    const isOnlyPlace = !hasRoad && (
                        x.type === "city" || x.type === "town" || x.type === "village" ||
                        x.type === "municipality" || x.type === "hamlet" || x.type === "suburb"
                    );
                    return !isOnlyPlace;
                }).slice(0, 5);

                setItems(results);

                // log("SUGGESTIONS SET", results.length);

            } catch (e) {
                if (e.name !== "AbortError") {
                    // log("FETCH ERROR", e);
                    setItems([]);
                }
            } finally {
                setLoading(false);
            }
        })();

        return () => controller.abort();

    }, [debouncedTerm, minLength]); // ✅ city/state rimossi dai deps


    return (
        <Box position="relative" w="100%">
            <Input
                placeholder="Es: Via Roma 5, Via Garibaldi 12..."
                value={localValue}
                onChange={(e) => {
                    const v = e.target.value;

                    typingRef.current = Date.now();

                    setLocalValue(v);   // UI immediata
                    onChange?.(v);      // parent sync
                }}
                size="md"
                borderRadius="md"
                p={3}
                fontSize="md"
            />

            {loading && (
                <Box position="absolute" right="10px" top="50%" transform="translateY(-50%)">
                    <Spinner size="sm" color="gray.400" />
                </Box>
            )}

            {!!items.length && (
                <List
                    position="absolute"
                    bg="white"
                    zIndex="20"
                    border="1px solid #ccc"
                    borderRadius="md"
                    w="100%"
                    mt={1}
                    maxH="200px"
                    overflowY="auto"
                    shadow="md"
                >
                    {items.map((x) => (
                        <ListItem
                            key={x.place_id}
                            px={3}
                            py={2}
                            _hover={{ bg: "gray.100", cursor: "pointer" }}
                            fontSize="sm"
                            onClick={() => {
                                // ✅ label pulita: estrae road + civico invece del display_name completo
                                const a = x.address || {};
                                const road = a.road || a.pedestrian || a.path || a.footway || a.street || a.cycleway || "";
                                const number = a.house_number ? ` ${a.house_number}` : "";
                                const cleanLabel = road
                                    ? `${road}${number}`
                                    : (x.display_name || "").split(",")[0].trim();

                                // ⭐ BLOCCA sync parent → local
                                isUserTypingRef.current = true;
                                justSelectedRef.current = true; 

                                setLocalValue(cleanLabel);
                                onChange?.(cleanLabel);

                                onSelect?.({
                                    label: cleanLabel,  // ✅ label pulita, non display_name
                                    value: cleanLabel,
                                    lat: parseFloat(x.lat),
                                    lng: parseFloat(x.lon),
                                });

                                setItems([]);

                                // ⭐ sblocca dopo il render
                                setTimeout(() => {
                                    isUserTypingRef.current = false;
                                }, 0);
                            }}
                        >
                            {x.display_name}
                        </ListItem>
                    ))}
                </List>
            )}
        </Box>
    );
}